import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Dropdown from "react-bootstrap/Dropdown";
import Nav from "react-bootstrap/Nav";
import {
  ArrowLeft,
  Save,
  BookOpen,
  Plus,
  Eye,
  FileText,
  Gamepad2
} from "lucide-react";
import { getMe, getAdminLesson, saveAdminExercises } from "../../api/client";
import { useTranslation } from "../../hooks/useTranslation";
import ExerciseEditor from "../../components/teacher/ExerciseEditor";
import LiveQuizEditor from "../../components/teacher/LiveQuizEditor";

export default function QuizCreator() {
  const { t } = useTranslation();
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("exercises"); // NEU

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  const [lessonTitle, setLessonTitle] = useState("");
  const [exercises, setExercises] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, [moduleId, lessonId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const me = await getMe().catch(() => null);
      const data = await getAdminLesson(moduleId, lessonId);
      if (data) {
        setLessonTitle(data.lesson?.title || lessonId);
        // Nur normale exercises (ohne livequiz-Tag) in den Exercise-Tab laden
        const normalExercises = (data.quiz?.exercises || []).filter(
          (e) => !(Array.isArray(e.tags) && e.tags.includes("livequiz"))
        );
        setExercises(normalExercises);
      } else {
        setError(t.contentCreator?.lessonNotFound || "Lektion nicht gefunden");
      }
    } catch (err) {
      console.error("QuizCreator load error:", err);
      setError(t.contentCreator?.loadError || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Beim Speichern: aktuelle livequiz-exercises aus dem Backend holen und zusammenführen
      const data = await getAdminLesson(moduleId, lessonId);
      const lqExercises = (data?.quiz?.exercises || []).filter(
        (e) => Array.isArray(e.tags) && e.tags.includes("livequiz")
      );
      await saveAdminExercises(moduleId, lessonId, [...exercises, ...lqExercises]);
      setSuccess(t.contentCreator?.exercisesSaved || "Quiz erfolgreich gespeichert");
    } catch (err) {
      console.error("Save error:", err);
      setError(t.contentCreator?.saveError || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const addExercise = (type = "input") => {
    const exerciseNum = exercises.length + 1;
    const newExercise = {
      id: `${lessonId}_U${exerciseNum}`,
      title: `Übung ${exerciseNum}`,
      type,
      prompt: "",
      solution: "",
      points: 10,
      tags: [],
      time_limit_seconds: 120,
      config: getDefaultConfig(type)
    };
    setExercises([...exercises, newExercise]);
  };

  const getDefaultConfig = (type) => {
    switch (type) {
      case "input":
        return { answer: "", answer_format: "number", tolerance: 0, unit: "€", allow_text: true, extract_number: true, accepted_answers: [] };
      case "single_choice":
        return { choices: [{ id: "a", text: "", is_correct: true }, { id: "b", text: "", is_correct: false }, { id: "c", text: "", is_correct: false }, { id: "d", text: "", is_correct: false }] };
      case "dropdown":
        return { text_segments: [], blanks: [{ id: "b1", options: [{ id: "o1", text: "", is_correct: true }, { id: "o2", text: "", is_correct: false }] }] };
      default:
        return {};
    }
  };

  const updateExercise = (index, updatedExercise) => {
    setExercises(exercises.map((ex, i) => (i === index ? updatedExercise : ex)));
  };

  const moveExercise = (index, direction) => {
    const newExercises = [...exercises];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newExercises.length) return;
    [newExercises[index], newExercises[newIndex]] = [newExercises[newIndex], newExercises[index]];
    setExercises(newExercises);
  };

  const deleteExercise = (index) => {
    setExercises(exercises.filter((_, i) => i !== index));
  };

  const getTotalPoints = () => exercises.reduce((sum, ex) => sum + (ex.points || 0), 0);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">{t.common?.loading || "Lädt..."}</p>
      </Container>
    );
  }

  return (
    <main style={{ backgroundColor: "var(--bg-secondary)", minHeight: "100vh" }}>
      <Container className="py-4" style={{ maxWidth: "900px" }}>

        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div className="d-flex align-items-center">
            <Button variant="link" className="p-0 me-3 text-secondary" onClick={() => navigate("/teacher/content")}>
              <ArrowLeft size={24} />
            </Button>
            <div>
              <h1 className="mb-0 h3">
                <BookOpen size={24} className="me-2 text-primary" />
                {activeTab === "livequiz" ? "🎮 LiveQuiz bearbeiten" : (t.contentCreator?.editQuiz || "Quiz bearbeiten")}
              </h1>
              <div className="mt-1">
                <Badge bg="secondary" className="me-2">{moduleId}</Badge>
                <span className="text-muted small">{lessonTitle}</span>
              </div>
            </div>
          </div>

          {/* Save-Button nur im Exercises-Tab */}
          {activeTab === "exercises" && (
            <div className="d-flex gap-2">
              <Button
                variant={previewMode ? "primary" : "outline-secondary"}
                onClick={() => setPreviewMode(!previewMode)}
              >
                {previewMode ? <FileText size={16} className="me-1" /> : <Eye size={16} className="me-1" />}
                {previewMode ? (t.contentCreator?.editMode || "Bearbeiten") : (t.contentCreator?.preview || "Vorschau")}
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? <><Spinner animation="border" size="sm" className="me-1" />{t.common?.loading || "Lädt..."}</> : <><Save size={16} className="me-1" />{t.common?.save || "Speichern"}</>}
              </Button>
            </div>
          )}
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* NEU: Tab-Navigation */}
        <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={setActiveTab}>
          <Nav.Item>
            <Nav.Link eventKey="exercises">
              <FileText size={16} className="me-2" />
              Übungen
              <Badge bg="secondary" className="ms-2">{exercises.length}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="livequiz">
              <Gamepad2 size={16} className="me-2" />
              🎮 Live Quiz
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Tab: Übungen (bestehender Code) */}
        {activeTab === "exercises" && (
          <>
            {/* Stats */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="d-flex justify-content-between align-items-center py-3">
                <div className="d-flex gap-4">
                  <div>
                    <span className="text-muted small">{t.contentCreator?.exerciseCount || "Übungen"}</span>
                    <h4 className="mb-0">{exercises.length}</h4>
                  </div>
                  <div>
                    <span className="text-muted small">{t.contentCreator?.totalPoints || "Gesamtpunkte"}</span>
                    <h4 className="mb-0">{getTotalPoints()}</h4>
                  </div>
                </div>
                <Dropdown>
                  <Dropdown.Toggle variant="primary" id="add-exercise-dropdown">
                    <Plus size={16} className="me-1" />
                    {t.contentCreator?.addExercise || "Übung hinzufügen"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => addExercise("input")}>{t.contentCreator?.typeInput || "Texteingabe"}</Dropdown.Item>
                    <Dropdown.Item onClick={() => addExercise("single_choice")}>{t.contentCreator?.typeSingleChoice || "Single Choice"}</Dropdown.Item>
                    <Dropdown.Item onClick={() => addExercise("dropdown")}>{t.contentCreator?.typeDropdown || "Dropdown/Lückentext"}</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Card.Body>
            </Card>

            {previewMode ? (
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white">
                  <h5 className="mb-0">{t.contentCreator?.quizPreview || "Quiz-Vorschau"}</h5>
                </Card.Header>
                <Card.Body>
                  {exercises.length === 0 ? (
                    <p className="text-muted text-center py-4">{t.contentCreator?.noExercises || "Keine Übungen vorhanden"}</p>
                  ) : (
                    exercises.map((ex, idx) => (
                      <div key={ex.id || idx} className="mb-4 pb-4 border-bottom">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0"><Badge bg="secondary" className="me-2">{idx + 1}</Badge>{ex.title}</h6>
                          <Badge bg="info">{ex.points} {t.contentCreator?.points || "Punkte"}</Badge>
                        </div>
                        <p className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{ex.prompt}</p>
                        {ex.type === "input" && (
                          <div className="d-flex align-items-center gap-2">
                            <input type="text" className="form-control" style={{ maxWidth: "200px" }} placeholder={t.quiz?.enter_answer || "Antwort eingeben"} disabled />
                            {ex.config?.unit && <span className="text-muted">{ex.config.unit}</span>}
                          </div>
                        )}
                        {ex.type === "single_choice" && (
                          <div className="d-flex flex-column gap-2">
                            {(ex.config?.choices || []).map((choice) => (
                              <div key={choice.id} className="form-check">
                                <input type="radio" className="form-check-input" name={`preview-${ex.id}`} disabled />
                                <label className="form-check-label">{choice.text || `Option ${choice.id}`}</label>
                              </div>
                            ))}
                          </div>
                        )}
                        {ex.type === "dropdown" && (
                          <div>
                            {(ex.config?.text_segments || []).map((segment, sIdx) => (
                              <span key={sIdx}>
                                {segment}
                                {sIdx < (ex.config?.blanks?.length || 0) && (
                                  <select className="form-select d-inline-block mx-1" style={{ width: "auto" }} disabled>
                                    <option>{t.quiz?.choose || "-- Wählen --"}</option>
                                    {(ex.config?.blanks?.[sIdx]?.options || []).map((opt) => (
                                      <option key={opt.id}>{opt.text}</option>
                                    ))}
                                  </select>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </Card.Body>
              </Card>
            ) : exercises.length === 0 ? (
              <Card className="border-0 shadow-sm text-center py-5">
                <Card.Body>
                  <BookOpen size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">{t.contentCreator?.noExercises || "Noch keine Übungen"}</h5>
                  <p className="text-muted mb-3">{t.contentCreator?.addFirstExercise || "Fügen Sie die erste Übung hinzu"}</p>
                  <Dropdown>
                    <Dropdown.Toggle variant="primary">
                      <Plus size={16} className="me-1" />
                      {t.contentCreator?.addExercise || "Übung hinzufügen"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => addExercise("input")}>{t.contentCreator?.typeInput || "Texteingabe"}</Dropdown.Item>
                      <Dropdown.Item onClick={() => addExercise("single_choice")}>{t.contentCreator?.typeSingleChoice || "Single Choice"}</Dropdown.Item>
                      <Dropdown.Item onClick={() => addExercise("dropdown")}>{t.contentCreator?.typeDropdown || "Dropdown/Lückentext"}</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Card.Body>
              </Card>
            ) : (
              exercises.map((exercise, idx) => (
                <ExerciseEditor
                  key={exercise.id || idx}
                  exercise={exercise}
                  index={idx}
                  totalExercises={exercises.length}
                  onChange={(updated) => updateExercise(idx, updated)}
                  onMoveUp={() => moveExercise(idx, -1)}
                  onMoveDown={() => moveExercise(idx, 1)}
                  onDelete={() => deleteExercise(idx)}
                  t={t}
                />
              ))
            )}

            {/* Bottom Actions */}
            <div className="d-flex justify-content-between mt-4">
              <Button variant="outline-secondary" onClick={() => navigate(`/teacher/content/lesson/${moduleId}/${lessonId}`)}>
                <ArrowLeft size={16} className="me-1" />
                {t.contentCreator?.backToLesson || "Zur Lektion"}
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? <><Spinner animation="border" size="sm" className="me-1" />{t.common?.loading || "Lädt..."}</> : <><Save size={16} className="me-1" />{t.common?.save || "Speichern"}</>}
              </Button>
            </div>
          </>
        )}

        {/* NEU: Tab: Live Quiz */}
        {activeTab === "livequiz" && (
          <LiveQuizEditor moduleId={moduleId} lessonId={lessonId} lang="de" />
        )}

      </Container>
    </main>
  );
}
